// Restructure la description produit existante (H2 + listes/tableaux/paragraphes) en
// accordéons (Description / Caractéristiques / Détails techniques / Livraison & retours),
// un seul ouvert à la fois, plus un bloc FAQ séparé — sans réécrire le contenu original.
// Alimente aussi la modale "Guide des tailles" à partir des lignes de tableau existantes.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-patouno-product-description]').forEach(setupProductDescription);
  setupSizeGuideDialog();
  document.querySelectorAll('[data-patouno-sg]').forEach(setupInteractiveSizeGuide);
});

// Guide des tailles interactif : l'utilisateur entre la mesure décisive
// (tour de poitrine) → on recommande la taille et on surligne la ligne.
// Toutes les données viennent du DOM rendu par le snippet
// (snippets/patouno-size-guide-interactive.liquid), rien n'est codé en dur ici.
function setupInteractiveSizeGuide(root) {
  // Illustration Partie 1 : si le fichier image est absent (404), on bascule
  // automatiquement sur le schéma SVG de repli — la page n'est jamais cassée.
  const img = root.querySelector('[data-sg-img]');
  if (img) {
    const showFallback = () => {
      const figure = img.closest('.patouno-sg__figure--image');
      if (figure) figure.hidden = true;
      const fallback = root.querySelector('[data-sg-fallback]');
      if (fallback) fallback.hidden = false;
    };
    if (img.complete && img.naturalWidth === 0) showFallback();
    img.addEventListener('error', showFallback);
  }

  const input = root.querySelector('[data-sg-input]');
  const button = root.querySelector('[data-sg-button]');
  const result = root.querySelector('[data-sg-result]');
  const rows = [...root.querySelectorAll('[data-sg-row]')];
  if (!input || !result || !rows.length) return;

  // Tailles triées par mesure décisive croissante.
  const sizes = rows
    .map((row) => ({ row, name: row.dataset.size, value: parseFloat(row.dataset.decisive) }))
    .filter((s) => !Number.isNaN(s.value))
    .sort((a, b) => a.value - b.value);
  if (!sizes.length) return;

  const smallest = sizes[0];
  const largest = sizes[sizes.length - 1];
  const belowMsg = root.dataset.belowMsg || '';
  const aboveMsg = root.dataset.aboveMsg || '';
  const measureLabel = (root.dataset.decisiveLabel || 'mesure').toLowerCase();

  function clearHighlight() {
    sizes.forEach((s) => s.row.classList.remove('is-recommended'));
  }

  function render(size, note, state) {
    result.hidden = false;
    result.className = 'patouno-sg__result' + (state ? ' ' + state : '');
    const label = state === 'is-error' ? 'Attention' : 'Taille recommandée';
    const sizeText = state === 'is-error' ? 'Hors gamme' : size;
    result.innerHTML =
      '<p class="patouno-sg__result-label">' +
      label +
      '</p><p class="patouno-sg__result-size">' +
      sizeText +
      '</p><p class="patouno-sg__result-note">' +
      note +
      '</p>';
  }

  function recommend() {
    clearHighlight();
    const raw = String(input.value).replace(',', '.').trim();

    if (raw === '') {
      result.hidden = true;
      result.innerHTML = '';
      return;
    }

    const value = parseFloat(raw);
    if (Number.isNaN(value) || value <= 0) {
      render('', 'Entrez une valeur en cm pour obtenir votre recommandation.', 'is-warning');
      return;
    }

    // Trop petit : sous la plus petite taille.
    if (value < smallest.value) {
      smallest.row.classList.add('is-recommended');
      scrollRowIntoView(smallest.row);
      render(smallest.name, belowMsg, 'is-warning');
      return;
    }

    // Trop grand : au-dessus de la plus grande taille.
    if (value > largest.value) {
      render('', aboveMsg, 'is-error');
      return;
    }

    // Plus petite taille dont la mesure décisive est ≥ à celle du chien
    // (= on arrondit à la taille au-dessus quand on tombe entre deux).
    const chosen = sizes.find((s) => s.value >= value);
    chosen.row.classList.add('is-recommended');
    scrollRowIntoView(chosen.row);

    const isExact = chosen.value === value;
    const note = isExact
      ? 'Cette taille correspond au ' + measureLabel + ' de votre chien.'
      : 'Votre chien est entre deux tailles : nous recommandons la plus grande, la <strong>' +
        chosen.name +
        '</strong>, pour un vêtement confortable.';
    render(chosen.name, note);
  }

  function scrollRowIntoView(row) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    row.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  input.addEventListener('input', recommend);
  button?.addEventListener('click', () => {
    recommend();
    input.focus();
  });
}

function setupProductDescription(container) {
  const groupName = 'patouno-accordions-' + Math.random().toString(36).slice(2, 8);

  // La description Shopify est souvent enveloppée dans un unique <div> englobant
  // (style inline sur tout le contenu) : on déballe ce niveau pour retrouver les vrais
  // <h2>/<p>/<ul>/<table> au bon niveau.
  let root = container;
  while (root.children.length === 1 && root.children[0].tagName === 'DIV') {
    root = root.children[0];
  }

  const introNodes = [];
  const sections = [];
  let current = null;

  [...root.children].forEach((node) => {
    if (node.tagName === 'H2') {
      current = { title: node.textContent.trim(), nodes: [] };
      sections.push(current);
    } else if (current) {
      current.nodes.push(node);
    } else {
      introNodes.push(node);
    }
  });

  const faqIndex = sections.findIndex(
    (sec) =>
      sec.nodes.length > 0 &&
      sec.nodes.every((n) => n.tagName === 'P' && n.firstElementChild?.tagName === 'STRONG')
  );
  const faqSection = faqIndex > -1 ? sections.splice(faqIndex, 1)[0] : null;

  const buckets = { description: [...introNodes], caracteristiques: [], details: [] };
  let techTable = null;

  sections.forEach((sec) => {
    const table = sec.nodes.find((n) => n.tagName === 'TABLE');
    const list = sec.nodes.find((n) => n.tagName === 'UL' || n.tagName === 'OL');
    if (table) {
      techTable = table;
      buckets.details.push(...sec.nodes);
    } else if (list) {
      buckets.caracteristiques.push(...sec.nodes);
    } else {
      buckets.description.push(...sec.nodes);
    }
  });

  const wrapper = document.createElement('div');
  wrapper.className = 'patouno-product-accordions';

  function addAccordion(title, nodes, openByDefault) {
    if (!nodes.length) return;
    const details = document.createElement('details');
    details.className = 'patouno-faq__item';
    details.name = groupName;
    if (openByDefault) details.open = true;
    const summary = document.createElement('summary');
    summary.className = 'patouno-faq__question';
    summary.textContent = title;
    const answer = document.createElement('div');
    answer.className = 'patouno-faq__answer rte';
    answer.append(...nodes);
    details.append(summary, answer);
    wrapper.append(details);
  }

  addAccordion('Description', buckets.description, true);
  addAccordion('Caractéristiques', buckets.caracteristiques, false);
  addAccordion('Détails techniques', buckets.details, false);

  const shipDetails = document.createElement('details');
  shipDetails.className = 'patouno-faq__item';
  shipDetails.name = groupName;
  const shipSummary = document.createElement('summary');
  shipSummary.className = 'patouno-faq__question';
  shipSummary.textContent = 'Livraison & retours';
  const shipAnswer = document.createElement('div');
  shipAnswer.className = 'patouno-faq__answer rte';
  shipAnswer.innerHTML =
    '<ul><li>Livraison offerte en France.</li></ul>';
  shipDetails.append(shipSummary, shipAnswer);
  wrapper.append(shipDetails);

  container.replaceWith(wrapper);

  if (faqSection) {
    const faqBlock = document.createElement('div');
    faqBlock.className = 'patouno-product-faq-block';
    const heading = document.createElement('h2');
    heading.className = 'h2';
    heading.textContent = 'Questions fréquentes';
    const faqList = document.createElement('div');
    faqList.className = 'patouno-faq';

    faqSection.nodes.forEach((paragraph) => {
      const clone = paragraph.cloneNode(true);
      const strong = clone.querySelector('strong');
      const question = strong.textContent.trim();
      let afterStrong = strong.nextSibling;
      if (afterStrong && afterStrong.nodeName === 'BR') afterStrong = afterStrong.nextSibling;
      strong.remove();
      if (clone.firstChild && clone.firstChild.nodeName === 'BR') clone.firstChild.remove();

      const item = document.createElement('details');
      item.className = 'patouno-faq__item';
      const summary = document.createElement('summary');
      summary.className = 'patouno-faq__question';
      summary.textContent = question;
      const answer = document.createElement('div');
      answer.className = 'patouno-faq__answer';
      answer.append(...clone.childNodes);
      item.append(summary, answer);
      faqList.append(item);
    });

    faqBlock.append(heading, faqList);
    wrapper.after(faqBlock);
  }

  populateSizeGuide(techTable);
}

function populateSizeGuide(techTable) {
  const trigger = document.querySelector('[data-patouno-size-guide-trigger]');
  const dialog = document.querySelector('[data-patouno-size-guide-dialog]');
  const content = dialog?.querySelector('[data-patouno-size-guide-content]');
  if (!techTable || !trigger || !dialog || !content) return;

  const rows = [...techTable.querySelectorAll('tr')].filter((tr) => {
    const label = tr.querySelector('td, th')?.textContent || '';
    return /taille/i.test(label);
  });

  if (!rows.length) return;

  const table = document.createElement('table');
  const tbody = document.createElement('tbody');
  rows.forEach((tr) => tbody.append(tr.cloneNode(true)));
  table.append(tbody);
  content.append(table);
  trigger.hidden = false;
}

function setupSizeGuideDialog() {
  const trigger = document.querySelector('[data-patouno-size-guide-trigger]');
  const dialog = document.querySelector('[data-patouno-size-guide-dialog]');
  const closeBtn = dialog?.querySelector('[data-patouno-size-guide-close]');
  if (!trigger || !dialog) return;

  trigger.addEventListener('click', () => dialog.showModal());
  closeBtn?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
}
